
import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔧 Setting up auth state listener...');
    
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔔 Auth state change event:', {
          event,
          user: session?.user ? {
            id: session.user.id,
            email: session.user.email,
            provider: session.user.app_metadata?.provider
          } : null,
          sessionPresent: !!session
        });
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session
    console.log('🔍 Checking for existing session...');
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ Error getting session:', error);
      } else {
        console.log('📋 Existing session check:', {
          sessionFound: !!session,
          user: session?.user ? {
            id: session.user.id,
            email: session.user.email,
            provider: session.user.app_metadata?.provider
          } : null
        });
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      console.log('🧹 Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    console.log('🔗 Setting up native OAuth deep link handling');

    let removeListener: (() => void) | undefined;

    const handleAuthUrl = async (url?: string | null) => {
      console.log('📥 Received auth URL:', url);
      if (!url) return;
      const isCustomScheme = url.startsWith('shapeit://auth');
      const isUniversalLink =
        url.startsWith('https://shapeyourtown.net/auth') ||
        url.startsWith('https://www.shapeyourtown.net/auth');
      if (!isCustomScheme && !isUniversalLink) return;


      try {
        await Browser.close();
      } catch (closeError) {
        console.warn('⚠️ Browser close skipped:', closeError);
      }

      try {
        const parsed = new URL(url);
        const code = parsed.searchParams.get('code');
        const errorParam = parsed.searchParams.get('error');

        if (errorParam) {
          console.error('OAuth error:', errorParam);
        }

        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            console.error('❌ exchangeCodeForSession error:', exchangeError);
          } else {
            console.log('✅ Session established via PKCE');
          }
          return;
        }

        const hashSource = parsed.hash || (parsed.searchParams.get('hash') ? `#${parsed.searchParams.get('hash')}` : '');

        if (hashSource) {
          const hash = new URLSearchParams(hashSource.replace(/^#/, ''));
          const access_token = hash.get('access_token') || undefined;
          const refresh_token = hash.get('refresh_token') || undefined;

          if (access_token && refresh_token) {
            const { error: setErr } = await supabase.auth.setSession({ access_token, refresh_token });
            if (setErr) {
              console.error('❌ setSession error:', setErr);
            } else {
              console.log('✅ Session established via implicit flow');
            }
          } else {
            console.warn('⚠️ No code or tokens found in deep link.');
          }
          return;
        }

        console.warn('⚠️ Auth deep link received without code or tokens.');
      } catch (e) {
        console.error('❌ Deep link handling failed:', e);
      }
    };

    App.getLaunchUrl()
      .then(({ url }) => handleAuthUrl(url))
      .catch((error) => console.error('❌ getLaunchUrl failed:', error));

    App.addListener('appUrlOpen', ({ url }) => {
      void handleAuthUrl(url);
    }).then(handle => {
      removeListener = () => handle.remove();
    });

    return () => {
      if (removeListener) removeListener();
    };
  }, []);

  const signOut = async () => {
    console.log('🚪 Signing out user...');

    // Clean up push notification tokens BEFORE signOut (RLS requires auth.uid())
    try {
      const currentUserId = user?.id;
      if (currentUserId) {
        console.log('🔕 Removing device tokens for user before sign out');
        const { error: tokenDeleteError } = await supabase
          .from('device_tokens')
          .delete()
          .eq('user_id', currentUserId);
        if (tokenDeleteError) {
          console.warn('⚠️ Failed to delete device tokens:', tokenDeleteError.message);
        }
      }

      if (Capacitor.isNativePlatform()) {
        try {
          const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
          await FirebaseMessaging.deleteToken();
          console.log('✅ FCM token deleted on device');
        } catch (fcmError) {
          console.warn('⚠️ FCM deleteToken failed (non-fatal):', fcmError);
        }
      }
    } catch (cleanupError) {
      console.warn('⚠️ Push cleanup during sign out failed (non-fatal):', cleanupError);
    }

    try {
      // On native platforms, use 'global' scope to ensure server-side session invalidation
      // On web, use 'local' to avoid "session not found" errors with expired sessions
      const scope = Capacitor.isNativePlatform() ? 'global' : 'local';
      console.log(`🔧 Using sign out scope: ${scope} (native: ${Capacitor.isNativePlatform()})`);
      
      const { error } = await supabase.auth.signOut({ scope });
      if (error) {
        console.warn('⚠️ Sign out error:', error.message);
      }
    } catch (error) {
      console.warn('⚠️ Sign out failed:', error);
    }
    
    // Always clear local state regardless of API result
    console.log('🧹 Clearing local auth state');
    setSession(null);
    setUser(null);
  };


  const deleteAccount = async () => {
    console.log('🗑️ Deleting user account...');
    
    try {
      const { data, error } = await supabase.functions.invoke('delete-user-account');
      
      if (error) {
        console.error('❌ Error calling delete-user-account function:', error);
        throw error;
      }
      
      if (!data?.success) {
        console.error('❌ Delete account failed:', data?.error);
        throw new Error(data?.error || 'Failed to delete account');
      }
      
      console.log('✅ Account deleted successfully:', data);
      
      // Sign out after successful deletion
      await signOut();
    } catch (error) {
      console.error('❌ Error deleting account:', error);
      throw error;
    }
  };

  const value = {
    user,
    session,
    loading,
    signOut,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
